/**
 * Copyright © 2024-2026 Entangled Harmony Labs, LLC
 * All Rights Reserved. Proprietary and Confidential.
 */
// Audio Engine for Ear Training
// Uses Web Audio API for tone generation

export interface AudioSettings {
  volume: number;
  waveform: 'sine' | 'triangle' | 'square' | 'sawtooth';
  attackTime: number;
  releaseTime: number;
}

export interface PlaybackOptions {
  duration?: number;
  delay?: number;
  volume?: number;
}

export class AudioEngine {
  private audioContext: AudioContext | null = null;
  private masterGain: GainNode | null = null;
  private settings: AudioSettings;

  constructor(settings: Partial<AudioSettings> = {}) {
    this.settings = {
      volume: 0.3,
      waveform: 'triangle', // More guitar-like than sine
      attackTime: 0.01,
      releaseTime: 0.5,
      ...settings
    };
  }

  // Initialize audio context (must be called after user interaction)
  async initialize(): Promise<void> {
    if (!this.audioContext) {
      this.audioContext = new AudioContext();
      this.masterGain = this.audioContext.createGain();
      this.masterGain.connect(this.audioContext.destination);
      this.masterGain.gain.value = this.settings.volume;
    }

    // Resume if suspended (common on mobile)
    if (this.audioContext.state === 'suspended') {
      await this.audioContext.resume();
    }
  }

  // Convert note names to frequencies
  noteToFrequency(note: string, octave: number = 4): number {
    const A4 = 440;
    const noteOffsets: { [key: string]: number } = {
      'C': -9, 'C#': -8, 'Db': -8, 'D': -7, 'D#': -6, 'Eb': -6, 
      'E': -5, 'F': -4, 'F#': -3, 'Gb': -3, 'G': -2, 'G#': -1, 
      'Ab': -1, 'A': 0, 'A#': 1, 'Bb': 1, 'B': 2
    };
    
    const semitoneOffset = noteOffsets[note] + (octave - 4) * 12;
    return A4 * Math.pow(2, semitoneOffset / 12);
  }

  // Parse note string like "C4" or "F#3"
  parseNote(noteString: string): { note: string; octave: number } {
    const match = noteString.match(/^([A-G][#b]?)(\d+)$/);
    if (!match) {
      // Default to octave 4 if no octave specified
      return { note: noteString, octave: 4 };
    }
    return { note: match[1], octave: parseInt(match[2]) };
  }

  // Play a single note
  async playNote(noteString: string, options: PlaybackOptions = {}): Promise<void> {
    if (!this.audioContext) {
      await this.initialize();
    }

    if (!this.audioContext) return;

    const { note, octave } = this.parseNote(noteString);
    const frequency = this.noteToFrequency(note, octave);
    
    // Validate inputs to prevent non-finite values
    if (!isFinite(frequency) || frequency <= 0) {
      console.warn(`Invalid frequency: ${frequency} for note: ${noteString}`);
      return;
    }
    
    const duration = Math.max(100, options.duration || 1000); // Minimum 100ms
    const delay = Math.max(0, options.delay || 0);
    const volume = Math.max(0, Math.min(1, options.volume || this.settings.volume));

    const oscillator = this.audioContext.createOscillator();
    const gainNode = this.audioContext.createGain();
    
    oscillator.connect(gainNode);
    gainNode.connect(this.masterGain!);
    
    oscillator.frequency.value = frequency;
    oscillator.type = this.settings.waveform;
    
    // Envelope (ADSR - simplified to just attack and release)
    const now = this.audioContext.currentTime + delay;
    const attackTime = Math.max(0.001, this.settings.attackTime);
    const durationSeconds = duration / 1000;
    
    // Set gain values with validation
    gainNode.gain.setValueAtTime(0, now);
    gainNode.gain.linearRampToValueAtTime(volume, now + attackTime);
    
    // Ensure we don't try to ramp to 0 with exponentialRampToValueAtTime
    const endTime = now + durationSeconds;
    const releaseStartTime = Math.max(now + attackTime + 0.001, endTime - 0.1);
    gainNode.gain.exponentialRampToValueAtTime(0.001, endTime);
    
    oscillator.start(now);
    oscillator.stop(endTime);
  }

  // Play two notes as an interval
  async playInterval(
    rootNote: string, 
    intervalNote: string, 
    type: 'melodic' | 'harmonic' | 'ascending' | 'descending' = 'melodic',
    options: PlaybackOptions = {}
  ): Promise<void> {
    const duration = Math.max(500, options.duration || 1000);
    
    switch (type) {
      case 'harmonic':
        // Play both notes simultaneously
        await Promise.all([
          this.playNote(rootNote, { ...options, duration }),
          this.playNote(intervalNote, { ...options, duration })
        ]);
        break;
        
      case 'melodic':
      case 'ascending':
        // Play root first, then interval with proper timing
        const rootDuration = Math.floor(duration * 0.4);
        const intervalDuration = Math.floor(duration * 0.4);
        const gap = Math.floor(duration * 0.1);
        
        // Play root note
        this.playNote(rootNote, { ...options, duration: rootDuration });
        
        // Wait for root to finish, then play interval
        await new Promise(resolve => setTimeout(resolve, rootDuration + gap));
        await this.playNote(intervalNote, { ...options, duration: intervalDuration });
        break;
        
      case 'descending':
        // Play interval first, then root
        const intervalDurationDesc = Math.floor(duration * 0.4);
        const rootDurationDesc = Math.floor(duration * 0.4);
        const gapDesc = Math.floor(duration * 0.1);
        
        // Play interval note
        this.playNote(intervalNote, { ...options, duration: intervalDurationDesc });
        
        // Wait for interval to finish, then play root
        await new Promise(resolve => setTimeout(resolve, intervalDurationDesc + gapDesc));
        await this.playNote(rootNote, { ...options, duration: rootDurationDesc });
        break;
    }
  }

  // Play a chord (multiple notes simultaneously)
  async playChord(notes: string[], options: PlaybackOptions = {}): Promise<void> {
    const promises = notes.map(note => 
      this.playNote(note, options)
    );
    await Promise.all(promises);
  }

  // Play a scale sequence
  async playScale(notes: string[], options: PlaybackOptions = {}): Promise<void> {
    const noteDuration = (options.duration || 2000) / notes.length;
    
    for (let i = 0; i < notes.length; i++) {
      await this.playNote(notes[i], {
        ...options,
        duration: noteDuration * 0.8,
        delay: i * noteDuration / 1000
      });
    }
  }

  // Update settings
  updateSettings(newSettings: Partial<AudioSettings>): void {
    this.settings = { ...this.settings, ...newSettings };
    
    if (this.masterGain) {
      this.masterGain.gain.value = this.settings.volume;
    }
  }

  // Set master volume
  setVolume(volume: number): void {
    this.settings.volume = Math.max(0, Math.min(1, volume));
    if (this.masterGain) {
      this.masterGain.gain.value = this.settings.volume;
    }
  }

  // Get current settings
  getSettings(): AudioSettings {
    return { ...this.settings };
  }

  // Cleanup
  dispose(): void {
    if (this.audioContext) {
      this.audioContext.close();
      this.audioContext = null;
      this.masterGain = null;
    }
  }
}

// Interval calculations
export class IntervalCalculator {
  private static semitonesMap: { [key: string]: number } = {
    'P1': 0,   // Perfect Unison
    'R': 0,    // Root
    'm2': 1,   // Minor 2nd
    'M2': 2,   // Major 2nd
    'm3': 3,   // Minor 3rd
    'M3': 4,   // Major 3rd
    'P4': 5,   // Perfect 4th
    'TRI': 6,  // Tritone
    'P5': 7,   // Perfect 5th
    'm6': 8,   // Minor 6th
    'M6': 9,   // Major 6th
    'm7': 10,  // Minor 7th
    'M7': 11,  // Major 7th
    'P8': 12   // Perfect Octave
  };

  static intervalNames: { [key: string]: string } = {
    'P1': 'Perfect Unison',
    'R': 'Root',
    'm2': 'Minor 2nd',
    'M2': 'Major 2nd',
    'm3': 'Minor 3rd',
    'M3': 'Major 3rd',
    'P4': 'Perfect 4th',
    'TRI': 'Tritone',
    'P5': 'Perfect 5th',
    'm6': 'Minor 6th',
    'M6': 'Major 6th',
    'm7': 'Minor 7th',
    'M7': 'Major 7th',
    'P8': 'Perfect Octave'
  };

  // Calculate the note that is a given interval above the root
  static calculateIntervalNote(rootNote: string, interval: string): string {
    const audioEngine = new AudioEngine();
    const { note: rootNoteName, octave: rootOctave } = audioEngine.parseNote(rootNote);
    const semitones = this.semitonesMap[interval];
    
    if (semitones === undefined) {
      throw new Error(`Unknown interval: ${interval}`);
    }

    // Simple chromatic calculation instead of frequency conversion
    const notes = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];
    
    // Find root note index
    let rootIndex = notes.findIndex(n => n === rootNoteName);
    if (rootIndex === -1) {
      // Handle flat notes
      const flatToSharp: { [key: string]: string } = {
        'Db': 'C#', 'Eb': 'D#', 'Gb': 'F#', 'Ab': 'G#', 'Bb': 'A#'
      };
      const sharpNote = flatToSharp[rootNoteName] || rootNoteName;
      rootIndex = notes.findIndex(n => n === sharpNote);
      if (rootIndex === -1) rootIndex = 0; // Fallback to C
    }
    
    // Calculate target note
    const targetIndex = (rootIndex + semitones) % 12;
    const octaveChange = Math.floor((rootIndex + semitones) / 12);
    
    return `${notes[targetIndex]}${rootOctave + octaveChange}`;
  }

  // Get all available intervals
  static getAllIntervals(): string[] {
    return Object.keys(this.semitonesMap);
  }

  // Get interval name
  static getIntervalName(interval: string): string {
    return this.intervalNames[interval] || interval;
  }
}

// Global audio engine instance
export const audioEngine = new AudioEngine();